/**
 * Excluir e Lixeira (12/10/2026; regras L1–L8, desenho aprovado nos mockups):
 *  - BotaoExcluir "Excluir 🔒" (secundário, sem vermelho) nos cabeçalhos de Oportunidade, Proposta, Pedido e Ordem:
 *    o servidor avalia os vínculos; com impedimento, o modal diz o que impede e o caminho (só "Fechar");
 *    sem impedimento, identificação + vínculos verificados + avisos + justificativa obrigatória.
 *  - TelaLixeira (Administração › Lixeira): filtros, identificação, quem/quando/justificativa, restauração 🔒
 *    com conferência dos vínculos e justificativa. Nada expira e não há exclusão definitiva.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, consulta } from '../api.ts';
import { navegar } from '../rotas.ts';
import { useSessao } from '../sessao.tsx';
import { dataHora } from '../formatos.ts';
import { CampoBusca, EstadoCarga, Modal, useCarga } from './ui.tsx';

export type EntidadeLixeira = 'oportunidade' | 'proposta' | 'pedido' | 'ordem' | 'documento_operacional';
const permissaoDe = (e: EntidadeLixeira) => (e === 'documento_operacional' ? 'ordem' : e);
const ARTIGO: Record<EntidadeLixeira, { artigo: 'a' | 'o'; nome: string }> = {
  oportunidade: { artigo: 'a', nome: 'Oportunidade' },
  proposta: { artigo: 'a', nome: 'Proposta' },
  pedido: { artigo: 'o', nome: 'Pedido' },
  ordem: { artigo: 'a', nome: 'Ordem' },
  documento_operacional: { artigo: 'a', nome: 'Ordem' },
};

interface Registro {
  entidade: EntidadeLixeira;
  entidadeRotulo: string;
  id: string;
  numero: string;
  nome: string;
  tipo: string;
  situacao: string;
  clienteNome: string;
  migrado: boolean;
}
interface Avaliacao {
  registro: Registro;
  impedimentos: string[];
  verificados: string[];
  avisos: string[];
  efeitos: string[];
}

function Identificacao({ r }: { r: Registro }) {
  const pares: [string, string][] = [
    ['Tipo', r.entidadeRotulo],
    ['Número', r.numero],
    ['Nome', r.nome || '—'],
    ['Cliente', r.clienteNome],
    ['Modalidade', r.tipo],
    ['Situação', r.situacao],
  ];
  return (
    <dl className="lixeira-id" data-teste="lixeira-identificacao">
      {pares.map(([k, v]) => (
        <div key={k} className="lixeira-par">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Divide "O que impede: caminho" na linha do impedimento e na linha do caminho. */
function Impedimento({ texto }: { texto: string }) {
  const i = texto.indexOf(': ');
  return (
    <li>
      <span className="lixeira-bloq">✕ {i > 0 ? texto.slice(0, i) : texto}</span>
      {i > 0 && <span className="lixeira-caminho">Caminho: {texto.slice(i + 2)}</span>}
    </li>
  );
}

function Justificativa({ valor, setValor, exemplo }: { valor: string; setValor: (v: string) => void; exemplo: string }) {
  return (
    <label className="lixeira-justificativa">
      Justificativa (registrada na auditoria) *
      <textarea value={valor} onChange={(e) => setValor(e.target.value)} placeholder={exemplo} maxLength={1000} data-teste="lixeira-justificativa" />
    </label>
  );
}

/** "Excluir 🔒": só para quem tem a permissão de excluir na entidade. `aoExcluir` decide para onde a tela vai. */
export function BotaoExcluir({ entidade, id, aoExcluir }: { entidade: EntidadeLixeira; id: string; aoExcluir: () => void }) {
  const { pode } = useSessao();
  const [avaliacao, setAvaliacao] = useState<Avaliacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  if (!pode('excluir', permissaoDe(entidade))) return null;
  const abrir = async () => {
    setErro(null);
    setOcupado(true);
    try {
      setAvaliacao(await api.get<Avaliacao>(`/api/lixeira/avaliar${consulta({ entidade, id })}`));
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <>
      <button type="button" className="botao-secundario" onClick={() => void abrir()} disabled={ocupado} data-teste="excluir-registro">
        Excluir 🔒
      </button>
      {erro && (
        <span className="aviso aviso-erro" role="alert">
          {erro}
        </span>
      )}
      {avaliacao && <ModalExcluir avaliacao={avaliacao} aoFechar={() => setAvaliacao(null)} aoExcluir={aoExcluir} />}
    </>
  );
}

function ModalExcluir({ avaliacao, aoFechar, aoExcluir }: { avaliacao: Avaliacao; aoFechar: () => void; aoExcluir: () => void }) {
  const r = avaliacao.registro;
  const { artigo, nome } = ARTIGO[r.entidade];
  const tipoMin = nome.toLowerCase();
  const [just, setJust] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  if (avaliacao.impedimentos.length)
    return (
      <Modal titulo={`Não é possível excluir ${artigo} ${tipoMin}`} aoFechar={aoFechar} largo rodape={<button type="button" className="botao-primario" onClick={aoFechar}>Fechar</button>}>
        <div data-teste="lixeira-impedimento">
          <Identificacao r={r} />
          <p className="aviso aviso-alerta">
            <span className="rotulo-nivel">▲ Impedimento</span>
            {avaliacao.impedimentos.length === 1 ? 'Há um vínculo que impede a exclusão agora.' : `Há ${avaliacao.impedimentos.length} vínculos que impedem a exclusão agora.`} A exclusão é feita passo a passo: nada é enviado à Lixeira em cascata.
          </p>
          <div className="lixeira-secao">O que impede</div>
          <ul className="lixeira-lista">
            {avaliacao.impedimentos.map((t) => (
              <Impedimento key={t} texto={t} />
            ))}
          </ul>
          {avaliacao.verificados.length > 0 && (
            <>
              <div className="lixeira-secao">Sem impedimento</div>
              <ul className="lixeira-lista">
                {avaliacao.verificados.map((t) => (
                  <li key={t}>
                    <span className="lixeira-ok">✓</span> {t}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="texto-secundario">Nada foi alterado.</p>
        </div>
      </Modal>
    );
  const confirmar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      await api.post('/api/lixeira', { entidade: r.entidade, id: r.id, justificativa: just });
      aoFechar();
      aoExcluir();
    } catch (x) {
      setErro((x as Error).message);
      setOcupado(false);
    }
  };
  const valida = just.trim().length >= 4;
  return (
    <Modal
      titulo={`Excluir ${artigo} ${tipoMin}?`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!valida || ocupado} title={valida ? undefined : 'Informe a justificativa (mínimo de 4 caracteres)'} onClick={() => void confirmar()} data-teste="mover-lixeira">
            Mover para a Lixeira
          </button>
        </>
      }
    >
      <div data-teste="lixeira-confirmacao">
        <p className="sem-margem">
          Deseja mesmo excluir {artigo} {tipoMin} abaixo?
        </p>
        <Identificacao r={r} />
        {avaliacao.verificados.length > 0 && (
          <>
            <div className="lixeira-secao">Vínculos verificados</div>
            <ul className="lixeira-lista">
              {avaliacao.verificados.map((t) => (
                <li key={t}>
                  <span className="lixeira-ok">✓</span> {t}
                </li>
              ))}
            </ul>
          </>
        )}
        {avaliacao.avisos.length > 0 && (
          <ul className="lixeira-lista lixeira-avisos" data-teste="lixeira-avisos">
            {avaliacao.avisos.map((t) => (
              <li key={t}>▲ {t}</li>
            ))}
          </ul>
        )}
        <div className="lixeira-secao">Enquanto estiver na Lixeira</div>
        <ul className="lixeira-lista">
          {avaliacao.efeitos.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <p className="aviso aviso-info">
          {artigo === 'a' ? 'Ela' : 'Ele'} será enviad{artigo} à <strong>Lixeira</strong> e poderá ser restaurad{artigo} a qualquer momento por quem tem a permissão “Restaurar”. Não há exclusão definitiva automática. Nenhum registro relacionado é excluído, e a auditoria é preservada.
        </p>
        <Justificativa valor={just} setValor={setJust} exemplo="Ex.: registro criado por engano durante a adaptação" />
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ================= Administração › Lixeira ================= */

interface ItemLixeira {
  id: string;
  entidade: EntidadeLixeira;
  entidadeRotulo: string;
  numero: string;
  nome: string;
  tipo: string;
  cliente: { id: string | null; nome: string };
  excluidoEm: string;
  excluidoPor: string;
  justificativa: string;
  restaurado: { em: string; por: string; justificativa: string } | null;
  podeRestaurar: boolean;
  rota: string;
}
interface Restauracao {
  registro: Registro;
  impedimentos: string[];
  exclusao: { em: string; justificativa: string };
}

const diasDesde = (d: string) => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000));

export function TelaLixeira({ consultaInicial }: { consultaInicial: URLSearchParams }) {
  const [entidade, setEntidade] = useState('');
  const [situacao, setSituacao] = useState('na_lixeira');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [busca, setBusca] = useState('');
  const [por, setPor] = useState('');
  const destaque = consultaInicial.get('registro');
  const carga = useCarga(() => api.get<{ entidades: { id: string; rotulo: string }[]; itens: ItemLixeira[] }>(`/api/lixeira${consulta({ entidade, situacao, de, ate })}`), [entidade, situacao, de, ate]);
  const [restaurar, setRestaurar] = useState<ItemLixeira | null>(null);
  const [concluido, setConcluido] = useState<{ numero: string; rota: string; entidade: EntidadeLixeira } | null>(null);
  useEffect(() => {
    if (destaque) document.querySelector(`[data-lixeira="${destaque}"]`)?.scrollIntoView({ block: 'center' });
  }, [destaque, carga.dados]);
  const itens = useMemo(() => {
    const q = busca.trim().toLocaleLowerCase('pt-BR');
    return (carga.dados?.itens ?? []).filter((x) => (!por || x.excluidoPor === por) && (!q || [x.numero, x.nome, x.cliente.nome, x.justificativa].some((t) => t.toLocaleLowerCase('pt-BR').includes(q))));
  }, [carga.dados, busca, por]);
  const autores = [...new Set((carga.dados?.itens ?? []).map((x) => x.excluidoPor))].sort();
  return (
    <>
      <div className="titulo-pag-linha">
        <h1 className="titulo-pagina">Lixeira</h1>
      </div>
      <p className="texto-secundario">Oportunidades, Propostas, Pedidos e Ordens excluídos. Ficam aqui por tempo indeterminado (no mínimo 30 dias) e podem ser restaurados a qualquer momento; não há exclusão definitiva automática. Restaurar devolve o registro às listas com os mesmos dados e o mesmo número.</p>
      {concluido && (
        <p className="aviso aviso-sucesso" role="status" data-teste="lixeira-restaurado">
          {concluido.numero} restaurad{ARTIGO[concluido.entidade].artigo}. <a href={`#${concluido.rota}`}>Abrir</a>
        </p>
      )}
      <div className="barra-filtros" data-teste="lixeira-filtros">
        <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar na Lixeira" ajuda="Número, nome, cliente ou justificativa" teste="busca-lixeira" total={itens.length} />
        <label>
          Tipo
          <select value={entidade} onChange={(e) => setEntidade(e.target.value)} data-teste="filtro-tipo-lixeira">
            <option value="">Todos</option>
            {(carga.dados?.entidades ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label>
          Excluído por
          <select value={por} onChange={(e) => setPor(e.target.value)}>
            <option value="">Todos</option>
            {autores.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <label>
          Excluído de
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} />
        </label>
        <label>
          até
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
        </label>
        <label>
          Situação
          <select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-teste="filtro-situacao-lixeira">
            <option value="na_lixeira">Na Lixeira</option>
            <option value="restaurados">Restaurados</option>
            <option value="todos">Todos</option>
          </select>
        </label>
      </div>
      {!carga.dados ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="lixeira" />
      ) : itens.length === 0 ? (
        <p className="vazio" data-teste="lixeira-vazia">
          {situacao === 'na_lixeira' ? 'Nenhum registro na Lixeira.' : 'Nenhum registro encontrado.'}
        </p>
      ) : (
        <div className="rolagem quadro-tabela primeira-fixa">
          <table className="tabela" data-teste="tabela-lixeira">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Identificação</th>
                <th>Cliente</th>
                <th>Excluído em</th>
                <th>Por</th>
                <th>Justificativa</th>
                <th className="numero">Na Lixeira há</th>
                <th className="col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((x) => (
                <tr key={x.id} data-lixeira={x.id} className={x.id === destaque ? 'linha-destaque' : undefined}>
                  <td>
                    <span className="selo selo-neutro">{x.entidadeRotulo}</span>
                  </td>
                  <td>
                    <strong>{x.numero}</strong>
                    <div className="texto-secundario">{x.nome}</div>
                  </td>
                  <td>{x.cliente.nome}</td>
                  <td className="nowrap">{dataHora(x.excluidoEm)}</td>
                  <td>{x.excluidoPor}</td>
                  <td>
                    {x.justificativa}
                    {x.restaurado && (
                      <div className="texto-secundario">
                        Restaurado em {dataHora(x.restaurado.em)} por {x.restaurado.por}: {x.restaurado.justificativa}
                      </div>
                    )}
                  </td>
                  <td className="numero lixeira-dias">{x.restaurado ? '—' : `${diasDesde(x.excluidoEm)} dia(s)`}</td>
                  <td className="col-acoes">
                    {x.restaurado ? (
                      <a className="botao-secundario pequeno" href={`#${x.rota}`}>
                        Abrir
                      </a>
                    ) : (
                      x.podeRestaurar && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => setRestaurar(x)} data-teste="restaurar-lixeira">
                          Restaurar 🔒
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rodape-tabela">{itens.length} registro(s)</div>
        </div>
      )}
      {restaurar && (
        <ModalRestaurar
          item={restaurar}
          aoFechar={() => setRestaurar(null)}
          aoRestaurar={(r) => {
            setRestaurar(null);
            setConcluido(r);
            void carga.recarregar();
          }}
        />
      )}
    </>
  );
}

function ModalRestaurar({ item, aoFechar, aoRestaurar }: { item: ItemLixeira; aoFechar: () => void; aoRestaurar: (r: { numero: string; rota: string; entidade: EntidadeLixeira }) => void }) {
  const carga = useCarga(() => api.get<Restauracao>(`/api/lixeira/${item.id}/restauracao`), [item.id]);
  const [just, setJust] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const { artigo, nome } = ARTIGO[item.entidade];
  const r = carga.dados;
  const bloqueado = Boolean(r?.impedimentos.length);
  const valida = just.trim().length >= 4;
  const restaurar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      const x = await api.post<{ numero: string; rota: string; entidade: EntidadeLixeira }>(`/api/lixeira/${item.id}/restaurar`, { justificativa: just });
      aoRestaurar(x);
    } catch (e) {
      setErro((e as Error).message);
      setOcupado(false);
    }
  };
  return (
    <Modal
      titulo={`Restaurar ${artigo} ${nome.toLowerCase()}?`}
      aoFechar={aoFechar}
      largo
      rodape={
        bloqueado || !r ? (
          <button type="button" className="botao-primario" onClick={aoFechar}>
            Fechar
          </button>
        ) : (
          <>
            <button type="button" className="botao-secundario" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="button" className="botao-primario" disabled={!valida || ocupado} title={valida ? undefined : 'Informe a justificativa (mínimo de 4 caracteres)'} onClick={() => void restaurar()} data-teste="confirmar-restaurar">
              Restaurar
            </button>
          </>
        )
      }
    >
      {!r ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : (
        <div data-teste="lixeira-restauracao">
          <Identificacao r={r.registro} />
          <p className="texto-secundario sem-margem">
            Excluíd{artigo} em {dataHora(r.exclusao.em)} · Justificativa: {r.exclusao.justificativa}
          </p>
          {bloqueado ? (
            <>
              <div className="lixeira-secao">Conferência dos vínculos</div>
              <ul className="lixeira-lista">
                {r.impedimentos.map((t) => (
                  <Impedimento key={t} texto={t} />
                ))}
              </ul>
              <p className="aviso aviso-alerta">
                <span className="rotulo-nivel">▲ Impedimento</span>
                Resolva o que está acima e tente de novo. Nada foi alterado.
              </p>
            </>
          ) : (
            <>
              <div className="lixeira-secao">Conferência dos vínculos</div>
              <ul className="lixeira-lista">
                <li>
                  <span className="lixeira-ok">✓</span> Registro de origem ativo e sem conflito
                </li>
              </ul>
              <p className="aviso aviso-info">
                {artigo === 'a' ? 'Ela' : 'Ele'} volta às listas, buscas e indicadores com os mesmos dados e o mesmo número. Registros relacionados não são reativados nem alterados.
              </p>
              <Justificativa valor={just} setValor={setJust} exemplo="Ex.: excluído por engano" />
            </>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Para onde ir depois de excluir: a lista da entidade (o registro saiu dela). */
export const depoisDeExcluir = (rota: string) => () => navegar(rota);
